import React, { useEffect, useState } from 'react'
import queryString from "query-string";
import { FocaltecBillings, FocaltecBillingsQueryParams } from '../model/FocaltecBilling';
import Pagination, { getOffset, initialPage, initialPageSize } from '../components/Pagination';
import { getFocaltecBillings } from '../api/FocaltecBillingApi';
import { Button, Divider, Grid } from '@material-ui/core';
import BatteryCharging60Icon from '@material-ui/icons/BatteryCharging60';
import ValidatedInput from '../components/ValidatedInput';
import translate from "../i18n/Translator";
import { getTenants } from '../api/TenantAPI';
import DateRanges from '../components/DateRange';
import Gridable from '../components/Gridable';
import Ellipsis from '../components/Ellipsis';
import { ErrorSnackbar } from '../components/Snackbars';
import { Tenant, TenantsQueryParams } from '../model/Tenant';
import { dateToStringMonthAndYear } from '../components/DateFormat';

export default function FocaltecBilling() {
    const qs = queryString.parse(window.location.search);
    const paramsFromQueryString = () : FocaltecBillingsQueryParams => {
        return {
            tenantId: typeof qs["tenantId"] === "string" ? qs["tenantId"] as string : "",
            startDate: typeof qs["startDate"] === "string" ? "" : new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString(),
            endDate: typeof qs["endDate"] === "string" ? "" : new Date().toISOString()
        } as FocaltecBillingsQueryParams;
    }

    const [status, setStatus] = useState<string>("loading");
    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [currentTenant, setCurrentTenant] = useState<Tenant>()
    const [submitting, setSubmitting] = useState(false);
    const [data, setData] = useState<FocaltecBillings>();
    const [params, setParams] = useState<FocaltecBillingsQueryParams>(paramsFromQueryString);
    const [workingParams, setWorkingParams] = useState<FocaltecBillingsQueryParams>(paramsFromQueryString);

    const [page, setPage] = useState<number>(initialPage);
    const [pageSize, setPageSize] = useState<number>(initialPageSize);

    const [error, setError] = useState<string>();

    const load = () => {
        let offset = getOffset(page, pageSize);
        setStatus("loading");
        setSubmitting(true);
        getFocaltecBillings(params, pageSize, offset).then(response => {
            setData(response);
            setStatus("loaded");
            fillTenants()
            setSubmitting(false);
        }).catch(error => {
            setStatus(error.message);
            setSubmitting(false);
        })
    }

    const fillTenants = () => {
        getTenants(0, 0, { search: "", module: "ACCOUNTS_PAYABLE", license: "PAID" } as TenantsQueryParams).then((response) => {
            setTenants(response.items);
        }).catch((error) => {
        });
    }

    useEffect(() => {
      load();
    }, [page, pageSize, params])

    const onChangedPage = (page: number) => {
        setPage(page);
        setData(undefined);
    }

    const onChangedPageSize = (page: number, pageSize: number) => {
        setPage(page);
        setPageSize(pageSize);
        setData(undefined);
    }

    const onAppliedFilter = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if(new Date(workingParams.startDate) < new Date(new Date().setFullYear(new Date().getFullYear() - 2))) {
            setError(translate("internal.billing.invalida_date") as string);
            return;
        }
        setParams({...workingParams});
    }

    const onChangedTenant = (name: string, value: string) => {
        setCurrentTenant(tenants!.find(tenant => tenant.id == value))
        setWorkingParams({...workingParams, tenantId: value})
    };

    const onChangeRangeDate = (startDate: Date, endDate: Date) => {
        endDate.setHours(0, 0, 0);
        setWorkingParams({...workingParams, startDate: startDate.toISOString(), endDate: endDate.toISOString()})
    }

    const onCloseSnackbars = () => {
        setError(undefined);
    };
    
    return (
        <Pagination title={translate("internal.billing.title")} icon={<BatteryCharging60Icon />}
            page={page} pageSize={pageSize} count={data ? data.items.length : 0} total={data ? data.total : 0}
            onChangedPage={onChangedPage} onChangedPageSize={onChangedPageSize}>
            <form autoComplete="off" noValidate onSubmit={onAppliedFilter}>
                <Grid container alignItems="center" justify="flex-end" className="TableFilter" spacing={1}>
                    <Grid item xs={6} sm>
                        <ValidatedInput
                            type="text"
                            id="tenantId" name="tenantId"
                            label={translate("internal.billing.filter") as string}
                            value={workingParams.tenantId}
                            margin={"dense"}
                            autocompleteOptions={tenants}
                            autocompleteValue={currentTenant ?? undefined}
                            getId={tenant => tenant?.id}
                            getLabel={tenant => tenant.name}
                            disabled={submitting}
                            onValueChanged={onChangedTenant} />
                    </Grid>
                    <Grid item xs={6} sm>
                        <DateRanges onChange={onChangeRangeDate} startDate={new Date(workingParams.startDate)} endDate={new Date(workingParams.endDate)}/>
                    </Grid>
                    <Grid item xs="auto">
                        <Button type="submit" variant="outlined" color="secondary" size="medium">
                            {translate("buttons.search")}
                        </Button>
                    </Grid>
                </Grid>
            </form>
            <Divider />
            <Gridable
                items={data ? data.items : []}
                loading={status === "loading"}
                error={status !== "loading" && status !== "loaded" ? status : undefined}
                empty={translate("internal.billing.empty") as string}
                columns={[
                    {
                        title: translate("internal.billing.month") as string,
                        converter: (billing) => (dateToStringMonthAndYear(new Date(billing.date))),
                        xs: false,
                        sm: 2,
                        lg: 2,
                        xl: 2
                    },
                    {
                        title: translate("internal.billing.tenant") as string,
                        converter: (billing) => (
                            <div>
                                <Ellipsis text={billing.tenant_name || ""} /><br />
                                <Ellipsis text={billing.tenant_id || ""} uppercased={false} secondary />
                            </div>
                        ),
                        xs: 8,
                        sm: 5,
                        lg: 5,
                        xl: 3
                    },
                    {
                        title: translate("internal.billing.payable_documents") as string,
                        converter: (billing) => (billing.payable_documents),
                        justify: "center",
                        xs: 4,
                        sm: 3,
                        lg: 2,
                        xl: 2
                    },
                    {
                        title: translate("internal.billing.total_documents_tenant") as string,
                        converter: (billing) => (billing.total_documents_tenant),
                        justify: "center",
                        xs: false,
                        sm: 2,
                        lg: 3,
                        xl: 3
                    },
                ]} />
                <ErrorSnackbar message={error} onClose={onCloseSnackbars} />
        </Pagination >
    )
}